/** @jsx jsx */
import React, { useCallback, useMemo } from 'react';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Form, { FormFooter, FormSection } from '@atlaskit/form';
import ButtonGroup from '@atlaskit/button/button-group';
import Button, { LinkButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import type { ValueType } from '@atlaskit/select';
import SectionMessage from '@atlaskit/section-message';
import Link from '@atlaskit/link';
import { ModalButtonTrigger } from '@atlassian/entry-point-modal-trigger/ModalButtonTrigger';
import { useRouterActions } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	ApplicationLinkOAuth2ConfigInfo,
	ApplicationLinkRemoteOAuth2ApplicationLink,
	ApplicationLinkV3Data,
	ApplicationLinkV3Simple,
} from '../../../common/entities.tsx';
import type { SelectOption } from '../../../common/types.tsx';
import {
	ApplicationClientId,
	ApplicationClientSecret,
	ApplicationUrl,
	CopyableApplicationClientID,
	CopyableApplicationClientSecret,
	DisplayName,
	DisplayUrl,
	RedirectUrl,
	RemoteApplicationScopeMultiSelect,
} from '../../common/fields/index.tsx';
import { getApplinksAdminUrl } from '../../../common/interchange.tsx';
import { putOAuth2ConfigInfo, updateApplinkV3 } from '../../../rest/index.tsx';
import {
	APPLINKS_LIST_PAGE_URL,
	APPLINKS_LIST_PAGE_URL_LEGACY,
} from '../../../common/constants.tsx';
import { incomingCredentialsModalEntryPoint } from './incoming-credentials-modal/entrypoint.tsx';

const styles = cssMap({
	growField: {
		flexGrow: 1,
	},
	dividerStyle: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		width: '100%',
		marginTop: token('space.200'),
	},
	formSectionInner: {
		marginTop: token('space.200'),
	},
});

const IncomingCredentialsModalButton = (props: {
	applink: ApplicationLinkV3Data;
	onDisplayError: (err: string) => void;
}) => {
	const entryPointProps = useMemo(
		() => ({ applink: props.applink, onDisplayError: props.onDisplayError }),
		[props.applink, props.onDisplayError],
	);

	return (
		<ModalButtonTrigger
			name="updateIncomingLinkCredentials"
			entryPoint={incomingCredentialsModalEntryPoint}
			entryPointProps={entryPointProps}
			iconBefore={EditIcon}
		>
			Update incoming link credentials
		</ModalButtonTrigger>
	);
};

type OAuth2EditForm = {
	displayName: string;
	displayUrl: string;
	remoteApplicationSupportedScopes: ValueType<SelectOption, true>;
	applicationClientId?: string;
	applicationClientSecret?: string;
	redirectUrl?: string;
};

type ApplinksOAuth2EditPageContentsProps = {
	applink: ApplicationLinkV3Data;
	oauth2ConfigInfo: ApplicationLinkOAuth2ConfigInfo;
	partiallyLinkedMode?: boolean;
	onDisplayError: (err: string) => void;
};

const ApplinksOAuth2EditPageContents = (props: ApplinksOAuth2EditPageContentsProps) => {
	const { push } = useRouterActions();

	const getListPageUrl = useCallback(() => {
		if (fg('applinks-933-experimental-list-page')) {
			return APPLINKS_LIST_PAGE_URL;
		}
		return APPLINKS_LIST_PAGE_URL_LEGACY;
	}, []);

	const onFormSubmit = useCallback(
		async (data: OAuth2EditForm) => {
			try {
				const applinkUpdate: ApplicationLinkV3Simple = {
					id: props.applink.id,
					type: props.applink.type,
					system: props.applink.system,
					primary: props.applink.primary,
					isExtraAuthRequired: props.applink.isExtraAuthRequired,
					rpcUrl: props.applink.rpcUrl,

					// Only change these properties:
					name: data.displayName,
					displayUrl: data.displayUrl,
				};

				const scopes = data.remoteApplicationSupportedScopes
					? data.remoteApplicationSupportedScopes.map((scope) => scope.value)
					: [];

				const oauth2ConfigUpdate: ApplicationLinkRemoteOAuth2ApplicationLink = {
					remoteClientId: data.applicationClientId,
					remoteClientSecret: data.applicationClientSecret,
					remoteRedirectUrl: data.redirectUrl,
					outboundScopes: scopes,
				};

				await updateApplinkV3(props.applink.id, applinkUpdate);
				await putOAuth2ConfigInfo(props.applink.id, oauth2ConfigUpdate);

				push(getListPageUrl());
			} catch (err) {
				props.onDisplayError(err instanceof Error ? err.message : 'Unknown error');
			}
		},
		[getListPageUrl, props, push],
	);

	return (
		<>
			<Form<OAuth2EditForm> onSubmit={onFormSubmit}>
				{({ formProps, submitting }) => (
					<form {...formProps}>
						<Stack space="space.200">
							<FormSection title="Link details">
								<Stack space="space.200" xcss={styles.formSectionInner}>
									<DisplayName name="displayName" defaultName={props.applink.name} />
									<DisplayUrl name="displayUrl" defaultUrl={props.applink.displayUrl} />
									<ApplicationUrl name="rpcUrl" defaultUrl={props.applink.rpcUrl} disabled />
								</Stack>
							</FormSection>
							<hr role="presentation" css={styles.dividerStyle} />
							{props.partiallyLinkedMode && (
								<SectionMessage title="Continue linking to the external application">
									<Text>
										In order to complete the link, enter the credentials for the incoming and
										outgoing links below.
									</Text>
								</SectionMessage>
							)}
							<FormSection
								title="Outgoing link"
								description="Copy these details to the external application. If you're not an admin of the application, ask an existing admin to make the updates for you."
							>
								<Stack space="space.200" xcss={styles.formSectionInner}>
									<Inline space="space.200">
										<Box xcss={styles.growField}>
											<CopyableApplicationClientID
												name="copyableApplicationClientId"
												clientId={props.oauth2ConfigInfo?.inboundOAuthClientId || ''}
											/>
										</Box>
										<Box xcss={styles.growField}>
											<CopyableApplicationClientSecret
												name="copyableApplicationClientSecret"
												clientSecret={props.oauth2ConfigInfo?.inboundOAuthClientSecret || ''}
											/>
										</Box>
									</Inline>
									{!props.oauth2ConfigInfo.isRemoteApplicationCloud && (
										<RemoteApplicationScopeMultiSelect
											name="remoteApplicationSupportedScopes"
											supportedScopes={
												props.oauth2ConfigInfo?.remoteApplicationSupportedScopes || []
											}
											defaultSelected={
												props.oauth2ConfigInfo?.outboundScopes?.map((scope) => ({
													label: scope,
													value: scope,
												})) || []
											}
										/>
									)}
								</Stack>
							</FormSection>
							<FormSection
								title="Incoming link"
								description="Find these details in the external application and enter them below. You may need to ask an admin of the external application for these details."
							>
								<Stack space="space.200" xcss={styles.formSectionInner}>
									{props.partiallyLinkedMode && (
										<Stack space="space.200">
											<Inline space="space.200">
												<Box xcss={styles.growField}>
													<ApplicationClientId name="applicationClientId" />
												</Box>
												<Box xcss={styles.growField}>
													<ApplicationClientSecret name="applicationClientSecret" />
												</Box>
											</Inline>
										</Stack>
									)}
									{!props.oauth2ConfigInfo.isRemoteApplicationCloud && (
										<RedirectUrl
											name="redirectUrl"
											defaultRedirectUrl={props.oauth2ConfigInfo?.inboundRedirectUrl || ''}
										/>
									)}
									{!props.partiallyLinkedMode && (
										<Box>
											<IncomingCredentialsModalButton
												applink={props.applink}
												onDisplayError={props.onDisplayError}
											/>
										</Box>
									)}
									<Link
										href={getApplinksAdminUrl(props.applink.displayUrl, props.applink.type)}
										target="_blank"
									>
										Go to {props.applink.displayUrl}
									</Link>
								</Stack>
							</FormSection>

							<FormFooter>
								<ButtonGroup label="Form submit options">
									<LinkButton href={getListPageUrl()} appearance="subtle">
										Cancel
									</LinkButton>
									<Button type="submit" appearance="primary" isLoading={submitting}>
										Save
									</Button>
								</ButtonGroup>
							</FormFooter>
						</Stack>
					</form>
				)}
			</Form>
		</>
	);
};

export default ApplinksOAuth2EditPageContents;
